import * as Phaser from 'phaser';
import { currentLang, isRTL, languages, setLang } from '../i18n';
import { onLangChange } from '../i18n/helpers';
import type { LangCode } from '../i18n/types';
import { TYPE } from '../render/palette';

const PILL_W = 130;
const PILL_H = 30;
const PANEL_W = 180;
const PANEL_ROW_H = 28;
const PANEL_MAX_VISIBLE = 12;
const DEPTH_DROPDOWN = 1000;
const DEPTH_BLOCKER = 999;

// Pill chrome — translucent glass over the dark backdrop.
const PILL_FILL = 0x141422;
const PILL_FILL_ALPHA = 0.6;
const PILL_FILL_HOVER_ALPHA = 0.8;
const BORDER = 0x88c8ff;
const BORDER_ALPHA = 0.12;
const BORDER_ALPHA_HOVER = 0.35;
// Panel rows.
const ROW_FILL = 0x141422;
const ROW_FILL_HOVER = 0x1f1f33;
const TEXT_COLOR = '#dddddd';
const TEXT_COLOR_HOVER = '#88c8ff';
const TEXT_COLOR_CURRENT = '#88c8ff';

const SCENE_W = 960;
const MARGIN = 16;

interface DropdownRow {
  container: Phaser.GameObjects.Container;
  bg: Phaser.GameObjects.Rectangle;
  label: Phaser.GameObjects.Text;
  code: LangCode;
}

export class LanguageDropdown {
  private readonly scene: Phaser.Scene;
  private readonly root: Phaser.GameObjects.Container;
  private readonly pillGfx: Phaser.GameObjects.Graphics;
  private readonly pillHit: Phaser.GameObjects.Rectangle;
  private readonly pillLabel: Phaser.GameObjects.Text;
  private readonly chevron: Phaser.GameObjects.Triangle;
  private panel: Phaser.GameObjects.Container | null = null;
  private blocker: Phaser.GameObjects.Rectangle | null = null;
  private maskGfx: Phaser.GameObjects.Graphics | null = null;
  private rows: DropdownRow[] = [];
  private scrollOffset = 0;
  private isOpen = false;

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
    this.root = scene.add.container(0, 0);
    this.root.setDepth(DEPTH_DROPDOWN);

    this.pillGfx = scene.add.graphics();
    this.paintPill(false);

    this.pillLabel = scene.add
      .text(0, 0, this.currentNativeName(), {
        color: TEXT_COLOR,
        fontFamily: TYPE.SANS,
        fontSize: '12px',
      })
      .setOrigin(0.5);
    this.chevron = scene.add.triangle(0, 0, 0, 0, 8, 0, 4, 5, BORDER, 0.6).setOrigin(0.5);

    // Transparent rect catches pointer events for the graphics-rendered pill.
    this.pillHit = scene.add
      .rectangle(0, 0, PILL_W, PILL_H, 0xffffff, 0)
      .setOrigin(0.5)
      .setInteractive({ useHandCursor: true });

    this.root.add([this.pillGfx, this.pillLabel, this.chevron, this.pillHit]);
    this.pillHit.on('pointerover', () => {
      this.paintPill(true);
      this.pillLabel.setColor(TEXT_COLOR_HOVER);
    });
    this.pillHit.on('pointerout', () => {
      this.paintPill(false);
      this.pillLabel.setColor(TEXT_COLOR);
    });
    this.pillHit.on('pointerdown', () => this.toggle());

    this.applyAnchor(isRTL() ? 'rtl' : 'ltr');
    onLangChange(scene, (_lang, dir) => {
      this.pillLabel.setText(this.currentNativeName());
      this.applyAnchor(dir);
      this.close();
    });

    const escHandler = (ev: KeyboardEvent): void => {
      if (ev.key === 'Escape' && this.isOpen) this.close();
    };
    window.addEventListener('keydown', escHandler);
    scene.events.once('shutdown', () => {
      window.removeEventListener('keydown', escHandler);
      this.root.destroy(true);
      this.panel?.destroy(true);
      this.blocker?.destroy();
      this.maskGfx?.destroy();
    });
  }

  /** Redraw the pill background. Pulled out so hover state can swap fills. */
  private paintPill(hover: boolean): void {
    const g = this.pillGfx;
    g.clear();
    g.fillStyle(PILL_FILL, hover ? PILL_FILL_HOVER_ALPHA : PILL_FILL_ALPHA);
    g.fillRoundedRect(-PILL_W / 2, -PILL_H / 2, PILL_W, PILL_H, PILL_H / 2);
    g.lineStyle(1, BORDER, hover ? BORDER_ALPHA_HOVER : BORDER_ALPHA);
    g.strokeRoundedRect(-PILL_W / 2, -PILL_H / 2, PILL_W, PILL_H, PILL_H / 2);
  }

  private applyAnchor(dir: 'ltr' | 'rtl'): void {
    if (dir === 'rtl') {
      this.root.setPosition(MARGIN + PILL_W / 2, MARGIN + PILL_H / 2);
      this.chevron.setPosition(-PILL_W / 2 + 16, 0);
      this.pillLabel.setPosition(8, 0);
    } else {
      this.root.setPosition(SCENE_W - MARGIN - PILL_W / 2, MARGIN + PILL_H / 2);
      this.chevron.setPosition(PILL_W / 2 - 16, 0);
      this.pillLabel.setPosition(-8, 0);
    }
  }

  private currentNativeName(): string {
    return languages().find((l) => l.code === currentLang())!.nativeName;
  }

  private toggle(): void {
    if (this.isOpen) this.close();
    else this.open();
  }

  private open(): void {
    if (this.isOpen) return;
    this.isOpen = true;

    this.blocker = this.scene.add
      .rectangle(SCENE_W / 2, 360, SCENE_W * 2, 720 * 2, 0x000000, 0)
      .setOrigin(0.5)
      .setDepth(DEPTH_BLOCKER)
      .setInteractive();
    this.blocker.on('pointerdown', () => this.close());

    const panelHeight = Math.min(PANEL_MAX_VISIBLE, languages().length) * PANEL_ROW_H;
    const dir = isRTL() ? 'rtl' : 'ltr';
    const panelX = dir === 'rtl' ? MARGIN : SCENE_W - MARGIN - PANEL_W;
    const panelY = MARGIN + PILL_H + 4;

    this.panel = this.scene.add.container(panelX, panelY);
    this.panel.setDepth(DEPTH_DROPDOWN);

    const panelBg = this.scene.add.graphics();
    panelBg.fillStyle(PILL_FILL, 0.92);
    panelBg.fillRoundedRect(0, 0, PANEL_W, panelHeight, 8);
    panelBg.lineStyle(1, BORDER, BORDER_ALPHA_HOVER);
    panelBg.strokeRoundedRect(0, 0, PANEL_W, panelHeight, 8);
    this.panel.add(panelBg);

    this.rows = languages().map((entry, i) => {
      const rowContainer = this.scene.add.container(0, i * PANEL_ROW_H);
      const rowBg = this.scene.add.rectangle(0, 0, PANEL_W, PANEL_ROW_H, ROW_FILL, 0).setOrigin(0);
      const isCurrent = entry.code === currentLang();
      const label = this.scene.add
        .text(dir === 'rtl' ? PANEL_W - 12 : 12, PANEL_ROW_H / 2, entry.nativeName, {
          color: isCurrent ? TEXT_COLOR_CURRENT : TEXT_COLOR,
          fontFamily: TYPE.SANS,
          fontSize: '13px',
        })
        .setOrigin(dir === 'rtl' ? 1 : 0, 0.5);
      rowContainer.add([rowBg, label]);
      rowBg.setInteractive({ useHandCursor: true });
      rowBg.on('pointerover', () => {
        rowBg.setFillStyle(ROW_FILL_HOVER, 1);
        label.setColor(TEXT_COLOR_HOVER);
      });
      rowBg.on('pointerout', () => {
        rowBg.setFillStyle(ROW_FILL, 0);
        label.setColor(isCurrent ? TEXT_COLOR_CURRENT : TEXT_COLOR);
      });
      rowBg.on('pointerdown', () => {
        setLang(entry.code);
        this.close();
      });
      this.panel!.add(rowContainer);
      return { container: rowContainer, bg: rowBg, label, code: entry.code };
    });

    // make.graphics(..., false) creates the Graphics off the display list so
    // it's only reachable via the mask. We have to destroy it explicitly on
    // close() or the bitmap accumulates one stale instance per open/close.
    this.maskGfx = this.scene.make.graphics({}, false);
    this.maskGfx.fillStyle(0xffffff);
    this.maskGfx.fillRect(panelX, panelY, PANEL_W, panelHeight);
    const geomMask = this.maskGfx.createGeometryMask();
    for (const row of this.rows) row.container.setMask(geomMask);

    this.scene.input.on('wheel', this.onWheel, this);
  }

  private onWheel = (_p: Phaser.Input.Pointer, _o: unknown, _dx: number, dy: number): void => {
    if (!this.isOpen) return;
    const maxScroll = Math.max(0, (languages().length - PANEL_MAX_VISIBLE) * PANEL_ROW_H);
    this.scrollOffset = Phaser.Math.Clamp(this.scrollOffset + dy * 0.5, 0, maxScroll);
    for (let i = 0; i < this.rows.length; i++) {
      this.rows[i]!.container.y = i * PANEL_ROW_H - this.scrollOffset;
    }
  };

  private close(): void {
    if (!this.isOpen) return;
    this.isOpen = false;
    this.scene.input.off('wheel', this.onWheel, this);
    this.panel?.destroy(true);
    this.panel = null;
    this.blocker?.destroy();
    this.blocker = null;
    this.maskGfx?.destroy();
    this.maskGfx = null;
    this.rows = [];
    this.scrollOffset = 0;
  }
}
